import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SectionHeader } from '../components/ui/SectionHeader';
import { getReleasesForClient } from '../services/releaseService';
import { Play, ArrowLeft, ExternalLink, Share2, Disc } from 'lucide-react';
import { Release } from '../types';
import { SEO } from '../components/SEO';

export const Releases: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [releases, setReleases] = useState<Release[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [playerType, setPlayerType] = useState<'spotify' | 'soundcloud'>('spotify');

  useEffect(() => {
    const loadReleases = async () => {
        const fetchedReleases = await getReleasesForClient();
        setReleases(fetchedReleases);
    };
    loadReleases();
  }, []);

  useEffect(() => {
    if (id && releases.length > 0) {
      const release = releases.find(r => r.id === id);
      if (release) {
        setSelectedRelease(release);
      } else {
        navigate('/releases', { replace: true });
      }
    } else if (!id) {
      setSelectedRelease(null);
    }
  }, [id, releases, navigate]);

  useEffect(() => {
     if (selectedRelease) {
         if (selectedRelease.spotifyId && !selectedRelease.soundcloudUrl) {
             setPlayerType('spotify');
         } else if (!selectedRelease.spotifyId && selectedRelease.soundcloudUrl) {
             setPlayerType('soundcloud');
         } else {
             setPlayerType('spotify');
         }
         window.scrollTo(0, 0);
     }
  }, [selectedRelease]);

  // Detail View
  if (selectedRelease) {
    const embedType = selectedRelease.spotifyEmbedType || 'track';
    
    const releaseStructuredData = {
        "@context": "https://schema.org",
        "@type": "MusicAlbum",
        "name": selectedRelease.title,
        "byArtist": {
            "@type": "MusicGroup",
            "name": selectedRelease.artist
        },
        "image": selectedRelease.cover,
        "datePublished": selectedRelease.date,
        "albumReleaseType": selectedRelease.type === 'Single' ? 'SingleRelease' : 'AlbumRelease',
        "url": `https://astralvibe.art/releases/${selectedRelease.id}`
    };

    return (
      <div className="min-h-screen relative animate-in fade-in slide-in-from-bottom-4 duration-500">
        <SEO 
            title={`${selectedRelease.title} by ${selectedRelease.artist}`}
            description={`Stream ${selectedRelease.title} by ${selectedRelease.artist} on AstralVibe. ${selectedRelease.type} release.`}
            image={selectedRelease.cover}
            url={`/releases/${selectedRelease.id}`}
            type="music.album"
            keywords={[selectedRelease.title, selectedRelease.artist, "new electronic music", "techno release", "afro house release"]}
            structuredData={releaseStructuredData}
        />
        {/* Background Blur Effect */}
        <div className="absolute inset-0 z-0 overflow-hidden">
             <div className="absolute inset-0 bg-astral-black/80 z-10"></div>
             <img src={selectedRelease.cover} alt="" className="w-full h-full object-cover blur-[100px] opacity-40 scale-110" />
        </div>

        <div className="relative z-20 container mx-auto px-6 py-12">
            {/* Navigation */}
            <button 
                onClick={() => navigate('/releases')}
                className="flex items-center gap-2 px-6 py-3 bg-black/40 backdrop-blur-md border border-white/10 rounded-full text-white hover:bg-white hover:text-black transition-all mb-12 group"
            >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-mono text-xs uppercase tracking-widest">Back to Archive</span>
            </button>

            <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-start">
                {/* Left: Cover Art */}
                <div className="relative aspect-square w-full max-w-xl mx-auto lg:mx-0 shadow-2xl shadow-black/50 border border-white/10 group">
                    <img 
                        src={selectedRelease.cover} 
                        alt={selectedRelease.title} 
                        className="w-full h-full object-cover"
                    />
                    {/* Vinyl shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                </div>

                {/* Right: Details */}
                <div className="flex flex-col justify-center h-full space-y-8">
                    <div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
                            <span className="px-3 py-1 bg-astral-purple/20 border border-astral-purple/50 text-astral-purple text-xs font-bold uppercase tracking-widest rounded-sm">
                                {selectedRelease.type}
                            </span>
                            <span className="text-slate-300 font-mono text-xs uppercase tracking-widest">
                                {selectedRelease.catalogNumber}
                            </span>
                             <span className="text-slate-300 font-mono text-xs uppercase tracking-widest">
                                {selectedRelease.date}
                            </span>
                        </div>
                        
                        <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-black uppercase text-white leading-none mb-2">
                            {selectedRelease.title}
                        </h1>
                        <h2 className="text-3xl text-slate-300 font-light">
                            {selectedRelease.artist}
                        </h2>
                    </div>

                    <div className="w-full h-[1px] bg-white/10"></div>

                    {/* Player Section */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-mono text-gray-500 uppercase tracking-widest flex items-center gap-2">
                                <Disc className="w-4 h-4" /> Audio Preview
                            </h3>
                            {selectedRelease.spotifyId && selectedRelease.soundcloudUrl && (
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => setPlayerType('spotify')}
                                        className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${playerType === 'spotify' ? 'bg-[#1DB954] text-black border-[#1DB954]' : 'border-white/20 text-gray-400 hover:text-white'}`}
                                    >
                                        Spotify
                                    </button>
                                    <button 
                                        onClick={() => setPlayerType('soundcloud')}
                                        className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${playerType === 'soundcloud' ? 'bg-[#ff5500] text-white border-[#ff5500]' : 'border-white/20 text-gray-400 hover:text-white'}`}
                                    >
                                        SoundCloud
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {playerType === 'spotify' && selectedRelease.spotifyId ? (
                             <div className="rounded-md overflow-hidden bg-black/50 shadow-lg border border-white/5">
                                <iframe 
                                    src={`https://open.spotify.com/embed/${embedType}/${selectedRelease.spotifyId}?utm_source=generator&theme=0`} 
                                    width="100%" 
                                    height="152" 
                                    frameBorder="0" 
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                                    loading="lazy"
                                    className="w-full"
                                    title={`Spotify Player - ${selectedRelease.title}`}
                                ></iframe>
                            </div>
                        ) : playerType === 'soundcloud' && selectedRelease.soundcloudUrl ? (
                            <div className="rounded-md overflow-hidden bg-black/50 shadow-lg border border-white/5">
                                <iframe 
                                    width="100%" 
                                    height="152" 
                                    scrolling="no" 
                                    frameBorder="no" 
                                    allow="autoplay" 
                                    src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(selectedRelease.soundcloudUrl)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                                    title={`SoundCloud Player - ${selectedRelease.title}`}
                                ></iframe>
                            </div>
                        ) : (
                            <div className="p-8 bg-white/5 border border-white/10 text-center text-gray-400 font-mono text-xs rounded-sm flex flex-col items-center justify-center gap-2">
                                <Disc className="w-8 h-8 opacity-20" />
                                <span>Preview Unavailable</span>
                                <span className="text-[10px] opacity-50">Stream ID not located</span>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-4 pt-4">
                        {selectedRelease.spotifyUrl && (
                            <a 
                                href={selectedRelease.spotifyUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 min-w-[200px] flex items-center justify-center gap-2 bg-[#1DB954] hover:bg-[#1ed760] text-black font-bold uppercase tracking-widest py-4 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" /> Open in Spotify
                            </a>
                        )}
                        {selectedRelease.soundcloudUrl && (
                            <a 
                                href={selectedRelease.soundcloudUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 min-w-[200px] flex items-center justify-center gap-2 bg-[#ff5500] hover:bg-[#ff7733] text-white font-bold uppercase tracking-widest py-4 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" /> Open in SoundCloud
                            </a>
                        )}
                        <button className="px-6 border border-white/20 text-white hover:bg-white hover:text-black transition-colors">
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
      </div>
    );
  }

  // Grid View
  const catalogStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": releases.map((release, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
            "@type": "MusicAlbum",
            "name": release.title,
            "byArtist": {
                "@type": "MusicGroup",
                "name": release.artist
            },
            "image": release.cover,
            "url": `https://astralvibe.art/releases/${release.id}`
        }
    }))
  };

  return (
    <div className="py-12">
      <SEO 
        title="Discography" 
        description="The AstralVibe sonic archive. Explore our latest releases, singles, and albums from the void." 
        url="/releases"
        keywords={["electronic music releases", "techno albums", "afro house singles", "music catalog", "astralvibe releases"]}
        structuredData={catalogStructuredData}
      />
      <div className="container mx-auto px-6">
        <SectionHeader subtitle="Discography" title="Sonic Archive" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {releases.map((release) => (
            <div 
                key={release.id} 
                onClick={() => navigate(`/releases/${release.id}`)}
                className="group cursor-pointer"
            >
              {/* Cover Art Card */}
              <div className="relative aspect-square overflow-hidden mb-6 bg-black border border-white/10 group-hover:border-astral-purple/50 transition-colors duration-300">
                <img 
                  src={release.cover} 
                  alt={release.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-4">
                    <span className="w-16 h-16 rounded-full border border-white/30 flex items-center justify-center text-white backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                      <Play className="w-6 h-6 ml-1 fill-white" />
                    </span>
                    <span className="text-white font-mono text-xs uppercase tracking-widest">View Transmission</span>
                </div>
              </div>
              
              {/* Meta Info */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-display font-bold uppercase text-white group-hover:text-astral-purple transition-colors">
                    {release.title}
                  </h3>
                  <p className="text-slate-300 text-sm tracking-widest uppercase mt-1 group-hover:text-white transition-colors">
                    {release.artist}
                  </p>
                </div>
                <div className="text-right">
                   <span className="block text-xs font-mono text-slate-500 group-hover:text-astral-purple transition-colors">{release.catalogNumber}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};